import { Injectable, computed, signal } from '@angular/core';

const CLAVE_SESION = 'escuela_isomaximus_token';

interface PayloadAlumno {
  exp?: number;
  sub?: number | string;
}

// Sesión del alumno de la Escuela ISOMaximus. Independiente a propósito de la sesión de la plataforma
// (tokenP) y de la de los cursos de cap7 (token_cursos): el backend rechaza con 401 un token del otro
// esquema (api.md -> Autenticación). Se guarda en localStorage y no en secureStorage (que usa
// sessionStorage) para que sobreviva a abrir un curso en otra pestaña; el token vence a las 12 h.
@Injectable({ providedIn: 'root' })
export class SesionAlumnoService {
  private readonly tokenActual = signal<string | null>(this.leerTokenVigente());
  readonly autenticado = computed(() => this.tokenActual() !== null);

  constructor() {
    // Un cierre de sesión (o un login) en otra pestaña se refleja aquí sin recargar.
    window.addEventListener('storage', (evento) => {
      if (evento.key === CLAVE_SESION) {
        this.tokenActual.set(this.leerTokenVigente());
      }
    });
  }

  // Revalida el vencimiento en cada lectura: una pestaña abierta más de 12 h no debe seguir enviando un
  // token que el backend ya rechazaría.
  get token(): string | null {
    const token = this.tokenActual();
    if (token && !this.estaVigente(token)) {
      this.cerrar();
      return null;
    }
    return token;
  }

  // Id del alumno que viaja en el token (payload.sub). Solo para la pantalla, p. ej. marcar en el foro los
  // mensajes propios: quién es el alumno lo decide siempre el backend a partir del token.
  get alumnoId(): number | null {
    const token = this.token;
    const id = Number(token ? this.decodificar(token)?.sub : NaN);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  iniciar(token: string): void {
    localStorage.setItem(CLAVE_SESION, token);
    this.tokenActual.set(token);
  }

  cerrar(): void {
    localStorage.removeItem(CLAVE_SESION);
    this.tokenActual.set(null);
  }

  private leerTokenVigente(): string | null {
    const token = localStorage.getItem(CLAVE_SESION);
    if (token && this.estaVigente(token)) {
      return token;
    }
    localStorage.removeItem(CLAVE_SESION);
    return null;
  }

  // Solo lee `exp` para no enviar tokens vencidos; la firma la valida exclusivamente el backend.
  private estaVigente(token: string): boolean {
    const payload = this.decodificar(token);
    return !!payload?.exp && payload.exp * 1000 > Date.now();
  }

  private decodificar(token: string): PayloadAlumno | null {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(base64));
    } catch {
      return null;
    }
  }
}
